/**
 * SocialCredentialsPanel — auto-generated per-platform credential forms.
 *
 * Reads schema from GET /api/fwtv/admin/social-credentials/schema so new
 * platforms show up automatically once their adapter is added to the
 * backend registry. Secret fields render as password inputs; non-secret
 * as text. Help text + help URLs shown under each field.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, ExternalLink, Save, Trash2 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });


export function SocialCredentialsPanel() {
  const [schema, setSchema] = useState(null);
  const [state, setState] = useState({});     // {platform: {field: value}}
  const [status, setStatus] = useState({});   // {platform: {configured, credentials}}

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/api/fwtv/admin/social-credentials/schema`, { headers: authH() }),
        axios.get(`${API}/api/fwtv/admin/social-credentials`, { headers: authH() }),
      ]);
      setSchema(s.data.schema);
      const byPlatform = {};
      for (const p of l.data.platforms) byPlatform[p.platform] = p;
      setStatus(byPlatform);
      // Seed non-secret + presence markers
      const init = {};
      for (const [platform, fields] of Object.entries(s.data.schema)) {
        init[platform] = {};
        const stored = byPlatform[platform]?.credentials || {};
        for (const f of fields) {
          init[platform][f.key] = f.secret ? '' : (stored[f.key] || '');
        }
      }
      setState(init);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  };
  useEffect(() => { load(); }, []);

  const save = async (platform) => {
    // Only include non-empty fields — empty leaves prior stored value untouched
    const payload = {};
    const fields = schema[platform] || [];
    for (const f of fields) {
      const v = state[platform]?.[f.key];
      if (v) payload[f.key] = v;
    }
    if (!Object.keys(payload).length) {
      toast.error('Enter at least one value before saving'); return;
    }
    try {
      await axios.put(`${API}/api/fwtv/admin/social-credentials/${platform}`,
                        { credentials: payload }, { headers: authH() });
      toast.success(`${platform} saved`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (platform) => {
    if (!confirm(`Remove ${platform} credentials?`)) return;
    try {
      await axios.delete(`${API}/api/fwtv/admin/social-credentials/${platform}`,
                          { headers: authH() });
      toast.success(`${platform} removed`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
  };

  if (!schema) return <div className="text-sm text-slate-500 p-6">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="social-creds-panel">
      {Object.entries(schema).map(([platform, fields]) => {
        const st = status[platform] || {};
        return (
          <Card key={platform} className="bg-slate-900/60 border-slate-800"
                 data-testid={`social-creds-card-${platform}`}>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-sm flex items-center gap-2">
                {platform.replace('_', ' ')}
                {st.configured
                  ? <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-0.5" />configured</Badge>
                  : <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]"><XCircle className="h-3 w-3 mr-0.5" />not configured</Badge>}
              </CardTitle>
              {st.configured && (
                <Button size="sm" variant="destructive"
                         onClick={() => remove(platform)}
                         data-testid={`social-creds-remove-${platform}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {fields.map(f => (
                <div key={f.key}>
                  <Label className="text-xs text-slate-400 flex items-center gap-2">
                    {f.label}
                    {f.secret && <span className="text-[10px] text-slate-500">(secret)</span>}
                    {st.credentials?.[f.key] === '***set***' && (
                      <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[10px]">stored</Badge>
                    )}
                  </Label>
                  <Input
                    type={f.secret ? 'password' : 'text'}
                    value={state[platform]?.[f.key] || ''}
                    onChange={e => setState(s => ({
                      ...s, [platform]: { ...(s[platform] || {}), [f.key]: e.target.value }
                    }))}
                    placeholder={f.secret && st.credentials?.[f.key] === '***set***'
                                    ? '••• stored — leave empty to keep' : ''}
                    className="bg-slate-900 border-slate-800 font-mono text-xs mt-1"
                    data-testid={`social-creds-input-${platform}-${f.key}`}
                  />
                  {f.help && <p className="text-[10px] text-slate-500 mt-1">{f.help}</p>}
                  {f.help_url && (
                    <a href={f.help_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] text-indigo-400 hover:underline mt-1">
                      Get credentials <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ))}
              <Button size="sm" onClick={() => save(platform)}
                       data-testid={`social-creds-save-${platform}`}>
                <Save className="h-3 w-3 mr-1" /> Save {platform.replace('_', ' ')}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default SocialCredentialsPanel;
